import React, { Children, Component, PropTypes } from 'react';
import { Icon } from 'react-fast-ui';
import LoginPage from './components/LoginPage';
import ApplicationModel from './ApplicationModel';
import { CONFIG_SHAPE } from '../util/constants/propTypes';
import { localStorageKeys } from '../util/constants';
import DAO from '../util/dao/index';
import getConfig from '../util/dao/helpers/getConfig';
import { isBlank } from 'underscore.string';
import { initializeInlineManualTracking } from '../util/InlineManualUtil';

const saveTokenToLocalStorage = token => {
  if (isBlank(token)) {
    // clear the localStorage
    localStorage.clear();
  } else {
    localStorage.setItem(localStorageKeys.FR_TOKEN, token);
  }
};

const getTokenFromLocalStorage = () => {
  const token = localStorage.getItem(localStorageKeys.FR_TOKEN);

  if (isBlank(token)) {
    return null;
  }

  return token.trim();
};

// for now we don't show anything while loading the config and token
const LOADING_INDICATOR = (
  <div
    className="display-flex align-items-center justify-content-center"
    style={{ height: '100%', textAlign: 'center' }}>
    <div>
      <Icon size="3x" name="spinner" animate="pulse"/>
    </div>
  </div>
);

const TOKEN_CHECK_TIMER = 15000;

/**
 * This is the top level application controller. It deals with loading the config, dao, and model, and provides some
 * handlers to the lower level components
 */
export default class Controller extends Component {
  static childContextTypes = {
    config: CONFIG_SHAPE,
    dao: PropTypes.instanceOf(DAO),
    onTokenChange: PropTypes.func,
    model: PropTypes.instanceOf(ApplicationModel)
  };

  state = {
    config: null,
    dao: null,
    model: null
  };

  getChildContext() {
    const { config, dao, model } = this.state;

    return { config, dao, model, onTokenChange: this.handleTokenChange };
  }

  componentDidMount() {
    this.attachAjaxHandler();
    this.loadConfig();
  }

  // TODO: rather than attach to all ajax handlers, we should provide a dao INSTANCE (rather than singleton)
  attachAjaxHandler() {
    $(document).ajaxSend((event, jqXhr, settings) => {
      const { config, model } = this.state;
      if (config === null || model === null) {
        return;
      }

      const { url } = settings;
      if (typeof url === 'string' && url.indexOf(config.SERVLET_URL) === 0) {
        if (model.isLoggedIn()) {
          jqXhr.setRequestHeader('Authorization', `bearer ${model.getToken()}`);
        }
      }
    });
  }

  /**
   * load Localytics into page
   * (from http://docs.localytics.com/dev/web.html#getting-started-web)
   * changing anything in this function seems to just break localytics
   */
  /* eslint-disable */
  initializeLocalytics() {
    const { LOCALYTICS_APP_KEY } = this.state.config;

    +function (l, y, t, i, c, s) { // eslint-disable-line
      l[ 'LocalyticsGlobal' ] = i;
      l[ i ] = function () {
        (l[ i ].q = l[ i ].q || []).push(arguments); // eslint-disable-line
      };
      l[ i ].t = +new Date;
      (s = y.createElement(t)).type = 'text/javascript';
      s.src = '//web.localytics.com/v3/localytics.min.js';
      (c = y.getElementsByTagName(t)[ 0 ]).parentNode.insertBefore(s, c);
      ll('init', LOCALYTICS_APP_KEY, {} /* Options */);
    }(window, document, 'script', 'll');
  }

  /* eslint-enable */

  /**
   * Load the application config from the config.json file and save it into the state
   */
  loadConfig() {
    getConfig()
      .then(config => {
        const dao = new DAO({ apiUrl: config.SERVLET_URL, fastServerUrl: config.FASTSERVER_URL });

        // update the config state variable
        this.setState({ config, dao }, () => {
          this.initializeLocalytics();
          this.loadToken();
        });
      });
  }

  /**
   * Pull the token out of localStorage and resolve it to a user object
   */
  loadToken() {
    const { dao } = this.state;
    const token = getTokenFromLocalStorage();

    if (token === null) {
      this.handleTokenChange(null);
    } else {
      dao.checkToken(token)
        .then(resp => this.handleTokenChange(resp))
        .catch(err => {
          console.error(err);
          this.handleTokenChange(null);
        });
    }
  }

  handleTokenChange = token => {
    const model = new ApplicationModel({ token });

    // the access token hasn't changed, do nothing
    if (this.state.model && model.getToken() === this.state.model.getToken()) {
      return;
    }

    // copy the string access token to localStorage
    saveTokenToLocalStorage(model.getToken());

    this.setState({ model }, () => {
      if (model.isLoggedIn()) {
        this.initializeTimer();

        initializeInlineManualTracking(this.state.config.INLINE_MANUAL_KEY, model.getUser(), model.getUserAccess())
          .catch(error => console.error(error, 'inline manual not initialized'));
      } else {
        this.clearTimer();
      }
    });
  };

  timer = null;
  initializeTimer = () => {
    this.clearTimer();
    this.timer = setTimeout(this.checkToken, TOKEN_CHECK_TIMER);
  };

  clearTimer = () => {
    if (this.timer) {
      clearTimeout(this.timer);
    }
    this.timer = null;
  };

  checkToken = () => {
    const { model, dao } = this.state;

    if (model === null || dao === null) {
      return;
    }

    if (!model.isLoggedIn()) {
      return;
    }

    dao.checkToken(model.getToken())
    // if the token fails, try the one from local storage
      .catch(e => dao.checkToken(getTokenFromLocalStorage()))
      .then(
        token => {
          try {
            this.handleTokenChange(token);
          } catch (e) {
            console.error('Error encountered while updating token', e);
          }
          this.initializeTimer();
        }
      )
      .catch(e => this.handleTokenChange(null));
  };

  render() {
    const { dao, model, config } = this.state;
    const { children } = this.props;

    // wait until everything is loaded before rendering app
    if (config === null || dao === null || model === null) {
      return LOADING_INDICATOR;
    }

    // show app if logged in, else show login page
    if (model.isLoggedIn()) {
      return Children.only(children);
    } else {
      return <LoginPage/>;
    }
  }
}


// WEBPACK FOOTER //
// ./src/js/common/Controller.jsx